<h2 mat-dialog-title>
  {{ data?.template ? 'Edit Reference Range' : 'Add Reference Range' }}
</h2>
<mat-dialog-content>
  <form [formGroup]="form" (ngSubmit)="submitRange()">
    <div class="form-section">
      <h3>Observation Details</h3>
      <div class="form-grid">
        <mat-form-field appearance="outline" class="full-span">
          <mat-label>Observation code (LOINC/SNOMED)</mat-label>
          <input matInput type="text" formControlName="code" [matAutocomplete]="auto" placeholder="Search by name or code...">
          @if(loadingCodes) {
            <mat-icon matSuffix class="spin">refresh</mat-icon>
          } @else {
            <mat-icon matSuffix>search</mat-icon>
          }
          <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayCodeOption">
            @for(option of filteredCodes$ | async; track option.code) {
              <mat-option [value]="option">
                <div class="autocomplete-option">
                  <div class="option-title">{{ option.display || option.code }}</div>
                  <div class="option-subtitle">
                    <span class="badge" [class.snomed]="option.system?.includes('snomed')">{{ option.system?.includes('snomed') ? 'SNOMED' : 'Local' }}</span>
                    {{ option.code }}
                  </div>
                </div>
              </mat-option>
            }
          </mat-autocomplete>
          <mat-error>Please select a valid observation code.</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-span">
          <mat-label>Display override (optional)</mat-label>
          <input matInput formControlName="display" placeholder="e.g. Potassium (Serum)">
          <mat-hint>Overrides the default terminology name for display purposes.</mat-hint>
        </mat-form-field>
      </div>
    </div>

    <div class="form-section">
      <h3>Reference Boundaries</h3>
      <div class="form-grid">
        <div class="value-pair">
          <mat-form-field appearance="outline">
            <mat-label>Range Meaning</mat-label>
            <mat-select formControlName="type">
              @for(option of rangeTypeOptions; track option.value) {
                <mat-option [value]="option.value">{{ option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Interpretation (Default)</mat-label>
            <mat-select formControlName="interpretation">
              <mat-option [value]="null">None</mat-option>
              @for(option of interpretationOptions; track option.value) {
                <mat-option [value]="option.value">{{ option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <div class="value-pair">
          <mat-form-field appearance="outline">
            <mat-label>Low value</mat-label>
            <input matInput type="number" formControlName="lowValue" placeholder="e.g. 3.5">
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Low unit</mat-label>
            <input matInput formControlName="lowUnit" placeholder="mmol/L" [matAutocomplete]="autoLowUnit">
            <mat-autocomplete #autoLowUnit="matAutocomplete">
              @for(unit of ucumUnitOptions; track unit) {
                <mat-option [value]="unit">{{ unit }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        </div>

        <div class="value-pair">
          <mat-form-field appearance="outline">
            <mat-label>High value</mat-label>
            <input matInput type="number" formControlName="highValue" placeholder="e.g. 5.2">
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>High unit</mat-label>
            <input matInput formControlName="highUnit" placeholder="mmol/L" [matAutocomplete]="autoHighUnit">
            <mat-autocomplete #autoHighUnit="matAutocomplete">
              @for(unit of ucumUnitOptions; track unit) {
                <mat-option [value]="unit">{{ unit }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        </div>
      </div>
    </div>

    <div class="form-section">
      <h3>Applicability</h3>
      <div class="form-grid">
        <mat-form-field appearance="outline" class="applies-to-field">
          <mat-label>Applies to</mat-label>
          <mat-select formControlName="appliesTo" multiple [compareWith]="compareConcepts">
            @for(option of appliesToOptions; track option.value) {
              <mat-option [value]="option.concept">{{ option.label }}</mat-option>
            }
          </mat-select>
          <mat-hint>Leave empty to apply to all patients.</mat-hint>
        </mat-form-field>

        <div class="age-group">
          <div class="age-field">
            <mat-form-field appearance="outline">
              <mat-label>Minimum age</mat-label>
              <input matInput type="number" formControlName="ageLowValue" placeholder="e.g. 18">
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Unit</mat-label>
              <mat-select formControlName="ageLowUnit">
                @for(option of ageUnitOptions; track option.value) {
                  <mat-option [value]="option.value">{{ option.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          <div class="age-field">
            <mat-form-field appearance="outline">
              <mat-label>Maximum age</mat-label>
              <input matInput type="number" formControlName="ageHighValue" placeholder="e.g. 65">
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Unit</mat-label>
              <mat-select formControlName="ageHighUnit">
                @for(option of ageUnitOptions; track option.value) {
                  <mat-option [value]="option.value">{{ option.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        </div>

        <mat-form-field appearance="outline" class="full-span">
          <mat-label>Notes / instructions</mat-label>
          <textarea matInput rows="3" formControlName="text" placeholder="Add narrative guidance or interpretation cues."></textarea>
        </mat-form-field>
      </div>
    </div>
  </form>
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button mat-button mat-dialog-close [disabled]="savingTemplate">Cancel</button>
  <button mat-raised-button color="primary" (click)="submitRange()" [disabled]="savingTemplate">
    @if(savingTemplate) {
      <mat-icon class="spin">hourglass_top</mat-icon>
    } @else {
      <mat-icon>save</mat-icon>
    }
    <span>{{ form.value.id ? 'Update Range' : 'Save Range' }}</span>
  </button>
</mat-dialog-actions>
